<template>
  <div class="intervention-areas-section">
    <h3 class="section-title">
      <i class="fas fa-bullseye"></i> Áreas de Intervenção Prioritária
    </h3>
    
    <div class="intervention-grid">
      <!-- Principais áreas de intervenção -->
      <div class="priority-areas">
        <h4 class="areas-title">Áreas Críticas</h4>
        <div class="areas-list">
          <div 
            class="area-card" 
            v-for="(area, index) in interventionAreas" 
            :key="'area-'+index"
            :class="getAreaSeverityClass(area.severity)"
          >
            <div class="area-header">
              <div class="area-severity-indicator" :class="getAreaSeverityClass(area.severity)"></div>
              <h5 class="area-title">{{ area.name }}</h5>
              <div class="area-severity-badge" :class="getAreaSeverityClass(area.severity)">
                {{ getAreaSeverityLabel(area.severity) }}
              </div>
            </div>
            <p class="area-description">{{ area.description }}</p>
            <div class="impact-indicators">
              <div class="impact-item">
                <span class="impact-label">Impacto:</span>
                <div class="impact-rating">
                  <i 
                    class="fas fa-circle" 
                    v-for="i in 5" 
                    :key="'impact-'+i"
                    :class="{ 'active': i <= area.impact }"
                  ></i>
                </div>
              </div>
              <div class="impact-item">
                <span class="impact-label">Urgência:</span>
                <div class="impact-rating">
                  <i 
                    class="fas fa-circle" 
                    v-for="i in 5" 
                    :key="'urgency-'+i"
                    :class="{ 'active': i <= area.urgency }"
                  ></i>
                </div>
              </div>
            </div>
          </div>
          <div class="no-data" v-if="interventionAreas.length === 0">
            <i class="fas fa-info-circle"></i>
            <span>Não foram identificadas áreas críticas neste período</span>
          </div>
        </div>
      </div>
      
      <!-- Ações recomendadas -->
      <div class="recommended-actions">
        <h4 class="actions-title">Ações Recomendadas</h4>
        <div class="actions-list">
          <div class="action-card" v-for="(action, index) in recommendedActions" :key="'action-'+index">
            <div class="action-type-indicator" :class="action.type"></div>
            <div class="action-content">
              <div class="action-header">
                <h5 class="action-title">{{ action.title }}</h5>
                <div class="action-type-badge" :class="action.type">
                  {{ getActionTypeLabel(action.type) }}
                </div>
              </div>
              <p class="action-description">{{ action.description }}</p>
              <div class="action-timeline">
                <div class="timeline-icon">
                  <i class="fas fa-calendar-alt"></i>
                </div>
                <div class="timeline-text">{{ action.timeline }}</div>
              </div>
            </div>
          </div>
          <div class="no-data" v-if="recommendedActions.length === 0">
            <i class="fas fa-info-circle"></i>
            <span>Não há ações recomendadas para este período</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    categoryData: Object,
    categoryName: String,
    month: Number
  },
  data() {
    return {
      interventionAreas: [],
      recommendedActions: []
    }
  },
  mounted() {
    this.analyzeInterventionAreas()
    this.generateRecommendedActions()
  },
  methods: {
    analyzeInterventionAreas() {
      if (!this.categoryData) return
      
      // Analisar os dados e identificar áreas de intervenção prioritária
      // Isto seria idealmente feito com base em algoritmos avançados de análise
      // Aqui estamos usando uma versão simplificada baseada nos dados disponíveis
      
      const areas = []
      
      // Verificar se há quedas significativas na avaliação
      if (this.hasSignificantRatingDrop()) {
        areas.push({
          name: 'Qualidade do Atendimento',
          description: `Houve uma queda significativa nas avaliações de atendimento no setor de ${this.categoryName}. Recomenda-se programas de capacitação e treinamento para os profissionais da área.`,
          severity: 'high',
          impact: 5,
          urgency: 4
        })
      }
      
      // Verificar se há problemas de infraestrutura
      if (this.hasInfrastructureIssues()) {
        areas.push({
          name: 'Infraestrutura',
          description: `As avaliações indicam problemas de infraestrutura nos estabelecimentos de ${this.categoryName}, afetando a experiência dos visitantes. Recomenda-se avaliação técnica e plano de melhorias.`,
          severity: 'medium',
          impact: 4,
          urgency: 3
        })
      }
      
      // Verificar problemas de preço/valor
      if (this.hasPriceValueIssues()) {
        areas.push({
          name: 'Relação Custo-Benefício',
          description: `Os visitantes estão reportando baixa relação custo-benefício no setor de ${this.categoryName}. Recomenda-se revisão de preços e estratégias para aumentar a percepção de valor.`,
          severity: this.hasSignificantRatingDrop() ? 'high' : 'medium',
          impact: 4,
          urgency: this.hasSignificantRatingDrop() ? 4 : 3
        })
      }
      
      // Verificar problemas de sazonalidade
      if (this.hasSeasonalityIssues()) {
        areas.push({
          name: 'Gestão de Sazonalidade',
          description: `O setor de ${this.categoryName} apresenta forte variação sazonal, com quedas significativas em períodos específicos. Recomenda-se estratégias para equilibrar a oferta durante o ano.`,
          severity: 'medium',
          impact: 3,
          urgency: 3
        })
      }
      
      // Verificar problemas de diversidade de oferta
      if (this.hasOfferDiversityIssues()) {
        areas.push({
          name: 'Diversidade de Oferta',
          description: `Há limitada diversidade de opções no setor de ${this.categoryName}, o que impacta a experiência geral dos visitantes. Recomenda-se incentivar novos empreendimentos e diversificação.`,
          severity: 'low',
          impact: 3,
          urgency: 2
        })
      }
      
      this.interventionAreas = areas
    },
    
    generateRecommendedActions() {
      if (!this.categoryData) return
      
      const actions = []
      
      // Gerar ações com base nas áreas de intervenção identificadas
      this.interventionAreas.forEach(area => {
        if (area.name === 'Qualidade do Atendimento') {
          actions.push({
            title: 'Programa de Capacitação em Atendimento',
            description: 'Implementar um programa de capacitação para profissionais do setor, focado em excelência no atendimento, hospitalidade e resolução de problemas.',
            type: 'training',
            timeline: 'Implementação em 1-3 meses'
          })
          
          actions.push({
            title: 'Sistema de Reconhecimento e Incentivos',
            description: 'Criar um sistema de certificação e reconhecimento para estabelecimentos com excelência em atendimento, com campanhas de divulgação para o público.',
            type: 'policy',
            timeline: 'Implementação em 3-6 meses'
          })
        }
        
        if (area.name === 'Infraestrutura') {
          actions.push({
            title: 'Diagnóstico de Infraestrutura',
            description: 'Realizar uma avaliação técnica da infraestrutura dos estabelecimentos, identificando pontos críticos e necessidades de melhoria.',
            type: 'assessment',
            timeline: 'Implementação em 1-2 meses'
          })
          
          actions.push({
            title: 'Linha de Financiamento para Melhorias',
            description: 'Criar linha de financiamento especial para que estabelecimentos possam realizar melhorias de infraestrutura, com condições facilitadas.',
            type: 'financial',
            timeline: 'Implementação em 6-12 meses'
          })
        }
        
        if (area.name === 'Relação Custo-Benefício') {
          actions.push({
            title: 'Estudo de Precificação e Valor Percebido',
            description: 'Realizar estudo comparativo de preços e valor percebido, identificando disparidades e oportunidades de ajuste estratégico.',
            type: 'assessment',
            timeline: 'Implementação em 2-3 meses'
          })
          
          actions.push({
            title: 'Campanha "Valor Justo"',
            description: 'Implementar campanha para estabelecimentos que aderirem a um compromisso de preço justo, com selo especial e divulgação para visitantes.',
            type: 'marketing',
            timeline: 'Implementação em 3-4 meses'
          })
        }
        
        if (area.name === 'Gestão de Sazonalidade') {
          actions.push({
            title: 'Calendário de Eventos Estratégicos',
            description: 'Desenvolver um calendário anual de eventos e promoções para equilibrar o fluxo turístico durante os períodos de baixa temporada.',
            type: 'planning',
            timeline: 'Implementação em 2-4 meses'
          })
        }
        
        if (area.name === 'Diversidade de Oferta') {
          actions.push({
            title: 'Incentivos para Novos Empreendimentos',
            description: 'Criar programa de incentivos fiscais e facilitação para abertura de novos estabelecimentos que ampliem a diversidade de opções no setor.',
            type: 'policy',
            timeline: 'Implementação em 6-12 meses'
          })
          
          actions.push({
            title: 'Mapeamento de Oportunidades',
            description: 'Realizar estudo de mercado para identificar nichos e oportunidades de negócio ainda não explorados no setor, com divulgação para empreendedores.',
            type: 'assessment',
            timeline: 'Implementação em 3-5 meses'
          })
        }
      })
      
      // Se não houver áreas identificadas, sugerir ações gerais
      if (this.interventionAreas.length === 0) {
        actions.push({
          title: 'Monitoramento Contínuo',
          description: `Implementar sistema de monitoramento contínuo das avaliações do setor de ${this.categoryName}, com alertas para quedas significativas.`,
          type: 'monitoring',
          timeline: 'Implementação imediata'
        })
        
        actions.push({
          title: 'Pesquisa de Satisfação',
          description: `Realizar pesquisa detalhada de satisfação com visitantes para identificar pontos de melhoria no setor de ${this.categoryName}.`,
          type: 'assessment',
          timeline: 'Implementação em 1-2 meses'
        })
      }
      
      this.recommendedActions = actions
    },
    
    // Métodos auxiliares para análise (simulados para demonstração)
    hasSignificantRatingDrop() {
      // Em um sistema real, isso analisaria padrões nos dados
      // Aqui estamos usando uma lógica simplificada
      if (!this.categoryData || !this.categoryData.average_data) return false
      
      // Simular uma chance de 30% de queda significativa
      return Math.random() < 0.3
    },
    
    hasInfrastructureIssues() {
      // Simular uma chance de 20% de problemas de infraestrutura
      return Math.random() < 0.2
    },
    
    hasPriceValueIssues() {
      // Simular uma chance de 40% de problemas de custo-benefício
      return Math.random() < 0.4
    },
    
    hasSeasonalityIssues() {
      // Maior chance de problemas de sazonalidade nos meses extremos (inverno e verão)
      const seasonalMonths = [1, 2, 6, 7, 8, 12]
      return seasonalMonths.includes(this.month) && Math.random() < 0.6
    },
    
    hasOfferDiversityIssues() {
      // Simular uma chance de 25% de problemas de diversidade
      return Math.random() < 0.25
    },
    
    // Métodos para formatação e apresentação
    getAreaSeverityClass(severity) {
      switch (severity) {
        case 'high': return 'critical'
        case 'medium': return 'warning'
        default: return 'info'
      }
    },
    
    getAreaSeverityLabel(severity) {
      switch (severity) {
        case 'high': return 'Crítico'
        case 'medium': return 'Atenção'
        default: return 'Monitorar'
      }
    },
    
    getActionTypeLabel(type) {
      switch (type) {
        case 'training': return 'Capacitação'
        case 'policy': return 'Política'
        case 'assessment': return 'Avaliação'
        case 'financial': return 'Financiamento'
        case 'marketing': return 'Marketing'
        case 'planning': return 'Planejamento'
        case 'monitoring': return 'Monitoramento'
        default: return 'Geral'
      }
    }
  }
}
</script>

<style scoped>
.intervention-areas-section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 20px;
  color: #0f172a;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.intervention-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 25px;
}

.priority-areas, .recommended-actions {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.areas-title, .actions-title {
  font-size: 16px;
  font-weight: 600;
  padding: 15px 20px;
  margin: 0;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.areas-list, .actions-list {
  padding: 20px;
  max-height: 500px;
  overflow-y: auto;
}

.area-card, .action-card {
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 15px;
  transition: transform 0.2s;
  position: relative;
  overflow: hidden;
}

.area-card:hover, .action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.area-card:last-child, .action-card:last-child {
  margin-bottom: 0;
}

.area-card {
  padding: 20px;
}

.area-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.area-severity-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 10px;
}

.area-severity-indicator.critical {
  background: #ef4444;
}

.area-severity-indicator.warning {
  background: #eab308;
}

.area-severity-indicator.info {
  background: #3b82f6;
}

.area-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  flex: 1;
}

.area-severity-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
}

.area-severity-badge.critical {
  background: #fee2e2;
  color: #b91c1c;
}

.area-severity-badge.warning {
  background: #fef3c7;
  color: #b45309;
}

.area-severity-badge.info {
  background: #dbeafe;
  color: #1e40af;
}

.area-description {
  font-size: 14px;
  line-height: 1.5;
  color: #475569;
  margin-bottom: 15px;
}

.impact-indicators {
  display: flex;
  gap: 20px;
}

.impact-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.impact-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.impact-rating {
  display: flex;
  gap: 2px;
}

.impact-rating i {
  font-size: 10px;
  color: #cbd5e1;
}

.impact-rating i.active {
  color: #324DDD;
}

.action-card {
  display: flex;
}

.action-type-indicator {
  width: 6px;
  flex-shrink: 0;
}

.action-type-indicator.training {
  background: #c084fc;
}

.action-type-indicator.policy {
  background: #3b82f6;
}

.action-type-indicator.assessment {
  background: #22c55e;
}

.action-type-indicator.financial {
  background: #eab308;
}

.action-type-indicator.marketing {
  background: #f59e0b;
}

.action-type-indicator.planning {
  background: #6366f1;
}

.action-type-indicator.monitoring {
  background: #10b981;
}

.action-content {
  padding: 15px 20px;
  flex: 1;
}

.action-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.action-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  flex: 1;
}

.action-type-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
}

.action-type-badge.training {
  background: #f5f3ff;
  color: #6d28d9;
}

.action-type-badge.policy {
  background: #dbeafe;
  color: #1e40af;
}

.action-type-badge.assessment {
  background: #dcfce7;
  color: #15803d;
}

.action-type-badge.financial {
  background: #fef3c7;
  color: #b45309;
}

.action-type-badge.marketing {
  background: #ffedd5;
  color: #c2410c;
}

.action-type-badge.planning {
  background: #e0e7ff;
  color: #4338ca;
}

.action-type-badge.monitoring {
  background: #d1fae5;
  color: #047857;
}

.action-description {
  font-size: 14px;
  line-height: 1.5;
  color: #475569;
  margin-bottom: 12px;
}

.action-timeline {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
}

.timeline-icon {
  color: #94a3b8;
}

.no-data {
  padding: 30px 20px;
  text-align: center;
  color: #64748b;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.no-data i {
  font-size: 24px;
  color: #94a3b8;
}

@media (max-width: 992px) {
  .intervention-grid {
    grid-template-columns: 1fr;
  }
}
</style> 